import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
  type TooltipContentProps,
  type TooltipPayloadEntry,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { BAR_MAX_SIZE, BAR_RADIUS_TOP, createCompactLegendContent } from './chart-styles.js';
import { formatPt } from '../../../lib/format.js';
import type {
  KpiPointProgressResponse,
  KpiPointProgressPeriodType,
  KpiTeam,
} from '../../../types/api.js';

interface PointProgressChartProps {
  /** pt進捗比較レスポンス */
  data: KpiPointProgressResponse | undefined;
  /** ローディング状態 */
  isLoading: boolean;
  /** 選択中の期間（'month:YYYY-MM' / 'quarter:YYYY-Qn' 形式） */
  selectedPeriod: string;
  /** 期間変更コールバック */
  onPeriodChange: (period: string) => void;
  /** 親のチームフィルタ。内訳ラベル解決に使用 */
  team: KpiTeam;
}

/** Progress Target バーの色（添付イメージの青系） @graph-connects none */
const PROGRESS_TARGET_COLOR = '#4285F4';
/** Progress Actual バーの色（添付イメージの赤系） @graph-connects none */
const PROGRESS_ACTUAL_COLOR = '#DB4437';

/** 期間 Select に出す直近月数 @graph-connects none */
const MONTH_OPTION_COUNT = 12;
/** 期間 Select に出す直近四半期数 @graph-connects none */
const QUARTER_OPTION_COUNT = 5;

/**
 * 日付が属する FY 四半期。FY は 7 月始まり（FY{Y} = (Y-1)/07/01 〜 Y/06/30）
 * @graph-connects none
 */
function fiscalQuarterOf(year: number, month: number): { fyEndYear: number; quarter: number } {
  if (month >= 7) {
    return { fyEndYear: year + 1, quarter: Math.floor((month - 7) / 3) + 1 };
  }
  return { fyEndYear: year, quarter: Math.floor((month - 1) / 3) + 3 };
}

/**
 * デフォルトの期間値（当月）を 'month:YYYY-MM' 形式で返す
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business pt進捗比較セクションの初期表示期間（当月）を生成
 * @graph-connects none
 */
export function getDefaultPointProgressPeriod(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `month:${String(y)}-${m}`;
}

/**
 * 'month:YYYY-MM' / 'quarter:YYYY-Qn' 形式の期間値を API パラメータに分解する
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business pt進捗比較セクションの期間 Select 値を periodType / period API パラメータへ変換
 * @graph-connects none
 */
export function parsePointProgressPeriod(value: string): {
  periodType: KpiPointProgressPeriodType;
  period: string;
} {
  const [type, period] = value.split(':');
  return {
    periodType: type === 'quarter' ? 'quarter' : 'month',
    period: period ?? '',
  };
}

/**
 * 期間 Select の選択肢（直近 N ヶ月 + 直近 N 四半期）を生成する
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business pt進捗比較セクションの期間選択肢を生成。月単体（直近12ヶ月）と FY四半期（直近5四半期、FY=7月始まり）
 * @graph-connects none
 */
export function buildPointProgressPeriodOptions(
  now: Date = new Date(),
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];

  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  for (let i = 0; i < MONTH_OPTION_COUNT; i++) {
    const mm = String(month).padStart(2, '0');
    options.push({ value: `month:${String(year)}-${mm}`, label: `${String(year)}/${mm}` });
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }

  let { fyEndYear, quarter } = fiscalQuarterOf(now.getFullYear(), now.getMonth() + 1);
  for (let i = 0; i < QUARTER_OPTION_COUNT; i++) {
    options.push({
      value: `quarter:${String(fyEndYear)}-Q${String(quarter)}`,
      label: `FY${String(fyEndYear)} ${String(quarter)}Q`,
    });
    quarter -= 1;
    if (quarter === 0) {
      quarter = 4;
      fyEndYear -= 1;
    }
  }

  return options;
}

/**
 * 内訳キーを表示ラベルの i18n キーへ解決する（週次/月次チャートと同じ規約）
 *  - team='pdg' の 'pdg' は「PDG (TE/SRE除く)」、team='ace' の 'ace' は「ACE (未割当)」
 * @graph-connects none
 */
function getBreakdownI18nKey(key: string, parentTeam: KpiTeam): string {
  if (parentTeam === 'pdg' && key === 'pdg') return 'kpi.weeklyActualBreakdown.pdgRest';
  if (parentTeam === 'ace' && key === 'ace') return 'kpi.weeklyActualBreakdown.aceRest';
  return `kpi.weeklyActualBreakdown.${key}`;
}

interface PointProgressTooltipProps {
  active?: boolean;
  payload?: readonly TooltipPayloadEntry[];
  label?: string | number;
  t: (key: string) => string;
}

/**
 * pt進捗比較 tooltip。目標 / 実績（+ 含まれる処理中pt）と達成率を表示する
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business pt進捗比較 tooltip。進捗目標pt・進捗実績pt・処理中pt・達成率をエンティティ単位で表示
 * @graph-connects none
 */
function PointProgressTooltipContent({ active, payload, label, t }: PointProgressTooltipProps) {
  if (active !== true || payload == null || payload.length === 0) return null;

  const row = payload[0]?.payload as
    | {
        progressTarget: number | null;
        progressActual: number;
        wipPt: number | null;
        achievementRate: number | null;
      }
    | undefined;
  if (!row) return null;

  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '8px 10px',
        opacity: 1,
        fontSize: 12,
        lineHeight: '18px',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      <div style={{ color: 'var(--color-text-primary)', marginBottom: 4, fontWeight: 500 }}>
        {String(label ?? '')}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        <li style={{ color: PROGRESS_TARGET_COLOR, padding: '1px 0' }}>
          {t('kpi.pointProgressTarget')}:{' '}
          {row.progressTarget !== null ? `${formatPt(row.progressTarget)}pt` : '-'}
        </li>
        <li style={{ color: PROGRESS_ACTUAL_COLOR, padding: '1px 0' }}>
          {t('kpi.pointProgressActual')}: {formatPt(row.progressActual)}pt
        </li>
        {row.wipPt !== null && row.wipPt > 0 && (
          <li style={{ color: 'var(--color-text-secondary)', padding: '1px 0' }}>
            {t('kpi.pointProgressWip')}: {formatPt(row.wipPt)}pt
          </li>
        )}
        {row.achievementRate !== null && (
          <li style={{ color: 'var(--color-text-primary)', padding: '1px 0' }}>
            {t('kpi.pointProgressAchievement')}: {row.achievementRate.toFixed(1)}%
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * pt進捗比較チャートカード
 *  - スプレッドシート `ace_まとめ` の Progress Target / Progress Actual グラフと同等の表示
 *  - 粒度は上部 team フィルタ連動（全体=PDG/ACE、ACE/PDG=チーム別、leaf team=メンバー別）
 *  - セクション個別の期間フィルタ（月単体 / FY四半期）を持つ
 *  - 当月は経過営業日按分の進捗目標 + 処理中pt 加算済み実績で比較
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business チーム/メンバー別の進捗目標pt vs 進捗実績pt 棒グラフカード。期間（月/FY四半期）Select 付き。
 *  当月は営業日按分の進捗目標と処理中pt込み実績で比較し、基準日を注記表示
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardHeader [calls] カードヘッダー描画
 * @graph-connects CardContent [calls] カードコンテンツ描画
 * @graph-connects Select [calls] 期間セレクト描画
 */
export function PointProgressChart({
  data,
  isLoading,
  selectedPeriod,
  onPeriodChange,
  team,
}: PointProgressChartProps) {
  const { t } = useTranslation();

  const periodOptions = useMemo(() => buildPointProgressPeriodOptions(), []);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.data.map((entry) => ({
      name: data.granularity === 'member' ? entry.label : t(getBreakdownI18nKey(entry.key, team)),
      progressTarget: entry.progressTarget,
      progressActual: entry.progressActual,
      wipPt: entry.wipPt,
      achievementRate: entry.achievementRate,
    }));
  }, [data, team, t]);

  const hasData = chartData.length > 0;
  // メンバー粒度などカテゴリ数が多い場合はラベルを斜めにして重なりを防ぐ
  const useAngledLabels = chartData.length > 6;
  // カテゴリ数が少ないと band が広く、maxBarSize だけでは目標/実績バーが
  // 各スロットの中央に離れて配置される。固定 barSize を指定するとバーが
  // カテゴリ中心にグループ化され、barGap 分だけ隣接して並ぶ
  const fixedBarSize = chartData.length <= 8 ? BAR_MAX_SIZE : undefined;

  return (
    <Card className="animate-content-reveal">
      <CardHeader title={t('kpi.pointProgress')} description={t('kpi.pointProgressDescription')} />
      <CardContent>
        <div className="flex justify-end mb-3">
          <Select
            options={periodOptions}
            value={selectedPeriod}
            onChange={(e) => {
              onPeriodChange(e.target.value);
            }}
            size="sm"
            aria-label={t('kpi.pointProgressPeriodSelect')}
          />
        </div>
        {isLoading ? (
          <div className="h-56 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : hasData ? (
          <>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  barGap={4}
                  margin={{ top: 20, right: 30, left: 10, bottom: useAngledLabels ? 30 : 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis
                    dataKey="name"
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    interval={0}
                    angle={useAngledLabels ? -30 : 0}
                    textAnchor={useAngledLabels ? 'end' : 'middle'}
                    height={useAngledLabels ? 60 : 30}
                  />
                  <YAxis
                    stroke="var(--color-text-muted)"
                    fontSize={11}
                    tickFormatter={(value: number) => formatPt(value)}
                  />
                  <Tooltip
                    wrapperStyle={{ opacity: 1, zIndex: 50 }}
                    filterNull={false}
                    content={(props: TooltipContentProps) => (
                      <PointProgressTooltipContent
                        active={props.active}
                        payload={props.payload}
                        label={props.label}
                        t={t}
                      />
                    )}
                  />
                  <Legend
                    content={createCompactLegendContent((value) => {
                      if (value === 'progressTarget') return t('kpi.pointProgressTarget');
                      if (value === 'progressActual') return t('kpi.pointProgressActual');
                      return value;
                    })}
                  />
                  <Bar
                    dataKey="progressTarget"
                    name="progressTarget"
                    fill={PROGRESS_TARGET_COLOR}
                    barSize={fixedBarSize}
                    maxBarSize={BAR_MAX_SIZE}
                    radius={BAR_RADIUS_TOP}
                  >
                    <LabelList
                      dataKey="progressTarget"
                      position="top"
                      fill="var(--color-text-primary)"
                      fontSize={11}
                      formatter={
                        ((value: number | null | undefined) =>
                          value != null && value > 0 ? formatPt(value) : '') as never
                      }
                    />
                  </Bar>
                  <Bar
                    dataKey="progressActual"
                    name="progressActual"
                    fill={PROGRESS_ACTUAL_COLOR}
                    barSize={fixedBarSize}
                    maxBarSize={BAR_MAX_SIZE}
                    radius={BAR_RADIUS_TOP}
                  >
                    <LabelList
                      dataKey="progressActual"
                      position="top"
                      fill="var(--color-text-primary)"
                      fontSize={11}
                      formatter={
                        ((value: number | null | undefined) =>
                          value != null && value > 0 ? formatPt(value) : '') as never
                      }
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {data?.includesCurrentMonth === true && data.asOfDate !== null && (
              <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                {t('kpi.pointProgressAsOfNote', { date: data.asOfDate })}
              </p>
            )}
          </>
        ) : (
          <div className="h-56 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
